import React, {useEffect, useState, useRef} from "react";
import {Button, Form, Input, Select, Switch, Tooltip} from "antd";
import {useFormik} from "formik";
import styled from "styled-components";
import {poolFormValidation} from "./poolFormValidation";
import {ErrorFocus} from "../../../_defaultComponents/ErrorFocus/ErrorFocus";
import {useTranslation} from "react-i18next";
import {CityFilter, PoolIntegrationTypeFilter} from "../../../_defaultComponents/FilterComponents";
import {managerCompanyOptions} from "../../../../constants/constants";
import {useStore} from "../../../../hooks/useStore";
import {useHistory, useLocation} from "react-router-dom";
import {PoolFormCompanyTable} from "../poolFormCompanyTable/PoolFormCompanyTable";
import {H3Map} from "../../../H3Map/H3Map";
import {useFetch} from "../../../../hooks/useFetch";
import {
    FormWrapper,
    FormRow,
    CityDisplayText,
    CompaniesSection,
    CompanyTableWrapper,
    SwitchRow,
    SwitchItem,
    SwitchLabel,
    SubmitRow,
    MapRow
} from "./styles/PoolForm.styled";

const defaultValues = {
    name: "",
    isActive: false,
    city: "",
    companyInfoArray: [],
    isTestPool: false,
    managerCompany: undefined
};
const genereteInitialValues = pool => {
    if (!pool) return defaultValues;
    const initialValues = {
        name: pool.name,
        isActive: pool.isActive,
        city: pool.city,
        companies: pool.companies.map(company => company._id.toString()),
        subCompanies: pool.subCompanies.map(subCompany => subCompany._id.toString()),
        companyInfoArray: pool.companies.map(company => ({
            company: company._id.toString(),
            subCompanies: pool.subCompanies
                .filter(subCompany => subCompany.companyId.toString() === company._id.toString())
                .map(subCompany => subCompany._id.toString())
        })),
        isTestPool: pool.isTestPool,
        managerCompany: pool.managerCompany
    };
    return initialValues;
};
export const PoolForm = ({
    pool = undefined,
    subCompanyOptions = [],
    type = "create", //or edit
    loadSubCompanies = () => {},
    onSelectedSubCompaniesChange = () => {}
}) => {
    const {t} = useTranslation(["pools"]);
    const stores = useStore();
    const history = useHistory();
    const location = useLocation();
    const [h3Cells, setH3Cells] = useState(() => {
        return pool?.h3Cells || [];
    });
    const mapRef = useRef(null);
    const [integrationType, setIntegrationType] = useState(pool?.integrationType || "pool");

    const getInitialCity = () => {
        if (type === "edit" && pool?.city) return pool.city;
        const searchParams = new URLSearchParams(location.search);
        const cityParam = searchParams.get("city");
        return cityParam || "";
    };

    useEffect(() => {
        const initialCity = getInitialCity();
        if (initialCity) {
            setTimeout(() => {
                if (mapRef.current) {
                    mapRef.current.panToCity(initialCity);
                }
            }, 1000);
        }
    }, [location.search, type, pool?.city]);

    const hasLoadedInitialSubCompanies = useRef(false);
    useEffect(() => {
        if (hasLoadedInitialSubCompanies.current) return;
        if (type !== "edit") return;
        if (!Array.isArray(pool?.companies) || pool.companies.length === 0) return;

        const companyIds = pool?.companies
            ?.map(company => company._id?.toString())
            ?.filter(Boolean);
        if (companyIds.length === 0) return;

        loadSubCompanies({companyIds});
        hasLoadedInitialSubCompanies.current = true;
    }, [type, pool?.companies, loadSubCompanies]);

    const formik = useFormik({
        initialValues: {
            ...(type === "edit" ? genereteInitialValues(pool) : defaultValues),
            city: getInitialCity()
        },
        validationSchema: poolFormValidation,
        enableReinitialize: true,
        validateOnBlur: false,
        validateOnChange: true,

        onSubmit: async values => {
            const companies = new Set();
            const subCompanies = new Set();
            values.companyInfoArray
                .filter(
                    companyInfo => companyInfo.subCompanies && companyInfo.subCompanies.length > 0
                )
                .forEach(companyInfo => {
                    companies.add(companyInfo.company);
                    companyInfo.subCompanies.forEach(subCompany => subCompanies.add(subCompany));
                });
            const payload = {
                name: values.name,
                city: values.city,
                isActive: values.isActive,
                companies: [...companies],
                subCompanies: [...subCompanies],
                integrationType: integrationType,
                isTestPool: values.isTestPool,
                h3Cells: h3Cells,
                managerCompany: values.managerCompany
            };

            try {
                const result = {hasError: true, poolId: undefined};
                if (type === "edit") {
                    const updateResult = await stores.poolStore.updatePool({
                        ...payload,
                        poolId: pool?._id?.toString()
                    });
                    result.hasError = !Boolean(updateResult?.failed?.length === 0);
                    result.poolId = updateResult.result?._id?.toString();
                } else if (type === "create") {
                    const createResult = await stores.poolStore.createPool(payload);
                    result.hasError = !Boolean(createResult?.success);
                    result.poolId = createResult.data?._id?.toString();
                }

                if (result.hasError) return;

                history.push(`/pools/${result.poolId}`);
            } catch (error) {}
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues
    } = formik;

    const [isCityH3CellsLoading, cityH3Cells] = useFetch({
        fetchFn: stores.poolStore.getPoolH3Cells.bind(null, {city: values.city}),
        dependencies: [values.city],
        initialCall: values.city !== ""
    });

    const otherHexagons = React.useMemo(() => {
        if (!cityH3Cells?.data || !Array.isArray(cityH3Cells.data)) {
            return [];
        }

        const hexagons = cityH3Cells.data.flatMap(pool => pool.h3Cells || []);

        return hexagons;
    }, [cityH3Cells]);

    useEffect(() => {
        refreshSelectedSubCompanies();
    }, [values.companyInfoArray, subCompanyOptions]);

    // Dedike Entegrasyon havuzlarda tek firma + tek alt firma kuralı: mevcutta
    // birden fazla firma/alt firma seçiliyse bu tip seçilemez.
    const companyInfosWithSubCompany = values.companyInfoArray.filter(
        companyInfo => companyInfo.subCompanies && companyInfo.subCompanies.length > 0
    );
    const selectedCompanyCount = companyInfosWithSubCompany.length;
    const selectedSubCompanyCount = new Set(
        companyInfosWithSubCompany.flatMap(companyInfo => companyInfo.subCompanies)
    ).size;
    const isDedicatedIntegrationDisabled =
        selectedCompanyCount > 1 || selectedSubCompanyCount > 1;

    const refreshSelectedSubCompanies = () => {
        const currentSelectedSubCompanies = new Set();
        values.companyInfoArray.forEach(companyInfo => {
            companyInfo.subCompanies.forEach(subCompany =>
                currentSelectedSubCompanies.add(subCompany)
            );
        });
        onSelectedSubCompaniesChange([...currentSelectedSubCompanies]);
    };

    const handleCityChange = city => {
        if (mapRef.current && city) {
            mapRef.current.panToCity(city);
        }
        setValues({
            ...values,
            city,
            companyInfoArray: [
                ...values.companyInfoArray.map(companyInfo => ({...companyInfo, subCompanies: []}))
            ],
            companies: [],
            subCompanies: []
        });
    };

    return (
        <FormWrapper>
            <Form
                autoComplete="off"
                data-testid="newUserForm"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleSubmit}
            >
                <ErrorFocus formik={formik} />
                <FormRow>
                    <div>
                        <Form.Item
                            label={t("fields.poolRegionName", {ns: "pools"})}
                            validateStatus={errors.name && touched.name ? "error" : "success"}
                            help={errors.name && touched.name ? errors.name : null}
                        >
                            <Input
                                name="name"
                                data-testid="poolName"
                                value={values.name}
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </div>
                    <div>
                        <Form.Item
                            label={t("fields.city", {ns: "common"})}
                            validateStatus={errors.city && touched.city ? "error" : "success"}
                            help={errors.city && touched.city ? errors.city : null}
                        >
                            {type === "create" ? (
                                <CityFilter
                                    name="city"
                                    data-testid="poolCity"
                                    value={values.city}
                                    onChange={handleCityChange}
                                    showAllOption={false}
                                    showLabel={false}
                                    selectMarginBottom="0px"
                                />
                            ) : (
                                <CityDisplayText>
                                    {values.city.charAt(0).toUpperCase() + values.city.slice(1)}
                                </CityDisplayText>
                            )}
                        </Form.Item>
                    </div>
                    <div>
                        <Form.Item label={t("filters.integrationType", {ns: "common"})}>
                            <PoolIntegrationTypeFilter
                                value={integrationType}
                                onChange={value => setIntegrationType(value)}
                                disabledTypes={
                                    isDedicatedIntegrationDisabled ? ["dedicatedIntegration"] : []
                                }
                                disabledTypeReason={t(
                                    "tooltips.dedicatedIntegrationNotAllowedForMulti",
                                    {ns: "pools"}
                                )}
                            />
                        </Form.Item>
                    </div>
                    <div>
                        <Form.Item
                            label={"Yönetici Firma"}
                            validateStatus={
                                errors.managerCompany && touched.managerCompany ? "error" : "success"
                            }
                            help={
                                errors.managerCompany && touched.managerCompany
                                    ? errors.managerCompany
                                    : null
                            }
                        >
                            <Select
                                data-testid="poolManagerCompany"
                                value={values.managerCompany}
                                onChange={value => setFieldValue("managerCompany", value)}
                                onBlur={() => formik.setFieldTouched("managerCompany", true)}
                            >
                                {managerCompanyOptions.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                    <CompaniesSection>
                        <label>{t("fields.companies", {ns: "pools"})}</label>
                        <CompanyTableWrapper>
                            <PoolFormCompanyTable
                                pool={pool}
                                formValues={values}
                                setFormValues={setValues}
                                subCompanyOptions={subCompanyOptions}
                                integrationType={integrationType}
                            />
                        </CompanyTableWrapper>
                    </CompaniesSection>

                    <SwitchRow>
                        <SwitchItem>
                            <SwitchLabel>{t("fields.status", {ns: "common"})}</SwitchLabel>
                            <Switch
                                checkedChildren={t("other.active", {ns: "common"})}
                                unCheckedChildren={t("other.passive", {ns: "common"})}
                                checked={values.isActive}
                                onChange={value => setFieldValue("isActive", Boolean(value))}
                            />
                        </SwitchItem>
                        <SwitchItem>
                            <SwitchLabel>{t("fields.testPool", {ns: "common"})}</SwitchLabel>
                            <Switch
                                checked={values.isTestPool}
                                onChange={value => setFieldValue("isTestPool", Boolean(value))}
                            />
                        </SwitchItem>
                    </SwitchRow>
                    <SubmitRow>
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            {type === "edit"
                                ? t("actions.update", {ns: "common"})
                                : t("actions.save", {ns: "common"})}
                        </Button>
                    </SubmitRow>
                    <MapRow>
                        <H3Map
                            ref={mapRef}
                            markers={(() => {
                                if (type === "edit" && Array.isArray(pool?.subCompanies)) {
                                    return pool.subCompanies.map(subCompany => ({
                                        _id: subCompany._id,
                                        name: subCompany.name,
                                        location: {
                                            latitude: subCompany.location?.latitude,
                                            longitude: subCompany.location?.longitude
                                        }
                                    }));
                                }

                                const selectedSubCompanyIds = values.companyInfoArray.flatMap(
                                    companyInfo => companyInfo.subCompanies || []
                                );

                                return selectedSubCompanyIds
                                    .map(subCompanyId => {
                                        const subCompany = subCompanyOptions.find(
                                            opt => opt._id === subCompanyId
                                        );
                                        if (!subCompany?.location) return null;
                                        return {
                                            _id: subCompany._id,
                                            name: subCompany.name,
                                            location: subCompany.location
                                        };
                                    })
                                    .filter(Boolean);
                            })()}
                            initialHexagons={h3Cells}
                            otherHexagons={otherHexagons}
                            onHexagonSelect={hexagons => {
                                setH3Cells(hexagons);
                            }}
                            showTileLayerControl={true}
                        />
                    </MapRow>
                </FormRow>
            </Form>
        </FormWrapper>
    );
};
